import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";

// API-tól várt aktivitás struktúra
interface ApiActivity {
  id: number;
  type: 'new_topic' | 'new_post';
  title: string;
  url: string;
  description?: string; // Csak 'new_topic' esetén
  content?: string;     // Csak 'new_post' esetén
  created_at: string;
  user: string;
  forum_category?: { id: number; name: string };
  forum_topic?: { id: number; title: string; forum_category: { id: number; name: string } };
}

const fetchRecentActivities = async (): Promise<ApiActivity[]> => {
  try {
    // API hívás az utolsó aktivitásokért
    const res = await fetch(`/api/activities/recent`);
    if (!res.ok) throw new Error('Az aktivitások lekérdezése sikertelen.');
    const apiData: ApiActivity[] = await res.json();

    return apiData;

  } catch (err) {
    console.error(err);
    throw err;
  }
};

const RecentCommentsStrip: React.FC<{ maxItemsPerView?: number }> = ({ maxItemsPerView = 1 }) => {
  const navigate = useNavigate();
  const { data: activities, isLoading, isError } = useQuery({
    queryKey: ['recentActivitiesStrip'],
    queryFn: fetchRecentActivities,
  });
  const [api, setApi] = useState<CarouselApi>();

  // Dinamikus basis osztály a maxItemsPerView alapján
  const getBasisClass = () => {
    switch (maxItemsPerView) {
      case 1:
        return 'md:basis-full lg:basis-full';
      case 2:
        return 'md:basis-1/2 lg:basis-1/2';
      default:
        return 'md:basis-1/2 lg:basis-1/3';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-48 bg-card rounded-lg">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full min-h-48 bg-card rounded-lg text-destructive">
        <AlertTriangle className="w-8 h-8 mr-2" /> Hiba
      </div>
    );
  }

  return (
    <div className="text-card-foreground border-border shadow-md h-full p-0 min-h-40 max-h-40 overflow-y-auto flex flex-col items-center justify-center">
      <Carousel
        setApi={setApi}
        opts={{
          align: "start",
          loop: true,
        }}
        className="w-full h-full relative"
      >
        <CarouselContent className="-ml-4">
          {(activities || []).map((activity) => (
            <CarouselItem key={`${activity.type}-${activity.id}`} className={`pl-4 ${getBasisClass()}`}>
              <div className="p-1 h-full">
                <button
                  onClick={() => {
                    if (activity.url) {
                      navigate(activity.url);
                    }
                  }}
                  className="block p-4 rounded-lg bg-secondary hover:bg-muted transition-colors h-full flex flex-col justify-between w-full text-left"
                >
                  <div>


                    <p className="text-sm line-clamp-2 italic text-muted-foreground">
                      "{(activity.content || activity.description || '').replace(/<[^>]+>/g, '')}"
                    </p>
                  </div>
                  <div className="mt-3 text-xs text-right">
                    <p className="font-semibold text-foreground">
                      {activity.user} a(z){' '}
                      <span className="font-semibold text-primary">
                        {activity.title}
                      </span>{' '}

                      témához
                    </p>
                    <p className="text-muted-foreground mt-1">
                      {new Date(activity.created_at).toLocaleString('hu-HU')}
                    </p>
                  </div>
                </button>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full z-10 px-2 pointer-events-none">
          <button
            onClick={() => api?.scrollPrev()}
            className="p-2 rounded-full bg-background/50 text-foreground hover:bg-background/80 transition-colors pointer-events-auto"
            aria-label="Előző"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => api?.scrollNext()}
            className="p-2 rounded-full bg-background/50 text-foreground hover:bg-background/80 transition-colors pointer-events-auto"
            aria-label="Következő"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </Carousel>
    </div>
  );
};

export default RecentCommentsStrip;
